import { useBunja } from 'bunja/react';
import { differenceInDays } from 'date-fns';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Badge } from '@flipster/react/design-system/badge';
import Formatted from '@flipster/react/design-utils/formatted';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { flex, vstack } from '@flipster/styled-system/patterns';
import { timestampToDate } from '@flipster/utils/datetime';

import { rewardHubV2Bunja } from '../../../state';
import { SpecialMission as SpecialMissionType } from '../../../types';
import { DistributionDate, ExpiredTime, MissionStatusBadges } from '../../misc';
import { ClaimButton } from '../../mission-cta-button';
import {
  getLocalizedText,
  getMissionStatusFlags,
  getRewardAmount,
  getRewardCurrency,
  isSpeedBonusProgress,
} from '../../utils';
import { getSingleMissionCoinIconConfig } from './coin-icon-config';

interface SpecialMissionProps {
  mission: SpecialMissionType;
}

export const SpecialMission = ({ mission }: SpecialMissionProps) => {
  const locale = useSupportedLocale();
  const { isMobile } = useBreakpoint();

  const { feeVoucherRewardAtom } = useBunja(rewardHubV2Bunja);
  const feeVoucherRewardRecord = useAtomValue(feeVoucherRewardAtom);

  const spec = mission?.spec;
  const missionStatus = mission?.status;

  if (!spec) return null;

  const { isAssigned, isValid, isExpired, isRejected } = getMissionStatusFlags(missionStatus);
  const completionReward = spec.speedBonus.completionRewards[0];
  const expiredAt = missionStatus?.progress.find((progress) => progress.type === 'SpeedBonus')?.expiresAt;
  const daysUntilExpiration = expiredAt ? differenceInDays(timestampToDate(expiredAt), new Date()) + 1 : 0;
  const isActive = isAssigned && isValid && !isExpired;

  const missionTitle = getLocalizedText(mission.skin?.body?.localizationTable, locale);
  const speedBonusProgress = missionStatus?.progress.find(isSpeedBonusProgress);

  return (
    <li>
      {isActive && <ExpiredTime daysUntilExpiration={daysUntilExpiration} type='special' />}
      <div
        className={css({
          display: { tablet: 'flex' },
          gap: { tablet: 20 },
          borderTopRadius: isActive ? undefined : 8,
          borderBottomRadius: 8,
          py: 16,
          px: { base: 12, tablet: 32 },
          background: 'background.default.level-1',
        })}
      >
        <div className={flex({ justify: 'space-between', alignItems: 'flex-start' })}>
          <div
            className={vstack({
              gap: 2,
              alignItems: { base: 'start', tablet: 'center' },
              minW: { tablet: 152 },
              pr: { tablet: 32 },
            })}
          >
            <img
              src={getSingleMissionCoinIconConfig(missionStatus).src}
              alt='mission icon'
              width={42}
              height={42}
              className={css({ display: { base: 'none', tablet: 'block' } })}
            />
            <Formatted.Number
              value={getRewardAmount(completionReward, feeVoucherRewardRecord)}
              precision={0}
              toFixed
              css={{
                textStyle: 'Title/L/Monospace',
                color: isActive ? 'content.accent.promotion' : 'content.default.level-1',
              }}
            />
            <div className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-1', mt: 4 })}>
              {getRewardCurrency(completionReward)}
            </div>
          </div>
          {isActive && (
            <div className={css({ display: { base: 'inline-flex', tablet: 'none' } })}>
              <Badge type='tint' color='positive'>
                <FormattedMessage id='Reward_hub.r251216.Special_reward' />
              </Badge>
            </div>
          )}
        </div>
        <div
          className={flex({ w: '100%', justify: 'space-between', alignItems: 'center', mt: { base: 16, tablet: 0 } })}
        >
          <div
            className={css({
              textStyle: 'Text/L/Strong',
              color: isActive ? 'content.default.level-5' : 'content.default.level-1',
            })}
          >
            <div
              className={flex({
                flexDir: { base: 'row', tablet: 'column' },
                align: { base: 'center', tablet: 'start' },
                gap: 8,
              })}
            >
              <MissionStatusBadges isExpired={isExpired} isRejected={isRejected} />
              {isActive && (
                <div className={css({ display: { base: 'none', tablet: 'inline-flex' } })}>
                  <Badge type='tint' color='positive'>
                    <FormattedMessage id='Reward_hub.r251216.Special_reward' />
                  </Badge>
                </div>
              )}
              {missionTitle}
            </div>
            <DistributionDate
              date={missionStatus?.settleAt}
              showMaintainMessage={false}
              isHide={isExpired || isRejected}
            />
          </div>
          {speedBonusProgress && (
            <ClaimButton
              size={isMobile ? 'small' : 'medium'}
              sectionIndex={mission.sectionIndex}
              missionIndex={mission.missionIndex}
              missionStatus={missionStatus}
              progress={speedBonusProgress}
              completionReward={completionReward}
            />
          )}
        </div>
      </div>
    </li>
  );
};
